import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import "@n8n/chat/style.css";
import { createChat } from "@n8n/chat";

import { IRootState } from "../../store";

const CHAT_CSS_VARIABLES: Record<string, string> = {
  "--chat--color--primary": "#2563eb",
  "--chat--color--primary-shade-50": "#1d4ed8",
  "--chat--color--primary--shade-100": "#1e3a8a",
  "--chat--color--secondary": "#10b981",
  "--chat--color-white": "#ffffff",
  "--chat--color-light": "#f3f4f6",
  "--chat--color-light-shade-50": "#e5e7eb",
  "--chat--color-light-shade-100": "#d1d5db",
  "--chat--color-dark": "#111827",
  "--chat--toggle--background": "#2563eb",
  "--chat--toggle--hover--background": "#1d4ed8",
  "--chat--toggle--active--background": "#1e3a8a",
  "--chat--toggle--color": "#ffffff",
  "--chat--toggle--size": "62px",
  "--chat--window--width": "410px",
  "--chat--window--height": "600px",
  "--chat--spacing": "1rem",
  "--chat--header-height": "auto",
  "--chat--header--padding": "1.6rem 1.6rem 1.1rem",
  //   "--chat--header--background": "linear-gradient(135deg,#111827 0%,#1f2937 45%,#0f172a 100%)",
  "--chat--header--background": "#2563eb",
  "--chat--header--color": "#f9fafb",
  "--chat--header--border-bottom": "none",
  "--chat--heading--font-size": "1.55rem",
  "--chat--subtitle--font-size": "0.95rem",
  "--chat--subtitle--line-height": "1.6",
  "--chat--message--font-size": "0.95rem",
  "--chat--message--padding": "1rem",
  "--chat--message--bot--background": "#ffffff",
  "--chat--message--bot--color": "#111827",
  "--chat--message--user--background": "#2563eb",
  "--chat--message--user--color": "#ffffff",
  "--chat--textarea--height": "56px",
  "--chat--border-radius": "10px",
  "--chat--window--border": "1px solid rgba(17,24,39,0.08)",
};

const resolveWebhookUrl = (): string => {
  const fromEnv =
    typeof import.meta.env.VITE_N8N_CHAT_WEBHOOK === "string"
      ? import.meta.env.VITE_N8N_CHAT_WEBHOOK
      : undefined;

  const fromWindow =
    typeof window !== "undefined" && (window as any).__N8N_CHAT_WEBHOOK
      ? String((window as any).__N8N_CHAT_WEBHOOK)
      : undefined;

  const fromDataset =
    typeof document !== "undefined" &&
    document.documentElement.dataset.n8nChatWebhook
      ? document.documentElement.dataset.n8nChatWebhook
      : undefined;

  const defaultWebhook =
    "https://n8n.srv1053022.hstgr.cloud/webhook/4b8548e1-17fc-4f8c-aec6-9594670b2a73/chat";

  return (fromEnv ?? fromDataset ?? fromWindow ?? defaultWebhook)
    .toString()
    .trim();
};

const N8nChatWidget = () => {
  const user = useSelector((state: IRootState) => state.user.user);
  const hasInitialized = useRef(false);
  const destroyRef = useRef<(() => void) | null>(null);
  const hasWarnedMissingWebhook = useRef(false);
  const previousCssValuesRef = useRef<Record<string, string>>({});
  const injectedStyleRef = useRef<HTMLStyleElement | null>(null);

  useEffect(() => {
    const webhookUrl = resolveWebhookUrl();

    if (!webhookUrl) {
      if (!hasWarnedMissingWebhook.current) {
        console.warn(
          "[N8nChatWidget] Missing VITE_N8N_CHAT_WEBHOOK environment variable. Chat widget will not initialize."
        );
        hasWarnedMissingWebhook.current = true;
      }
      return;
    }

    const userRole =
      user && typeof user === "object"
        ? ((user as any).user_role ?? (user as any).role ?? "").toString()
        : "";
    const isAdmin = userRole.toLowerCase() === "admin";

    if (!isAdmin) {
      return;
    }

    if (hasInitialized.current) {
      return;
    }

    if (!injectedStyleRef.current) {
      const style = document.createElement("style");
      style.setAttribute("data-sharepal-n8n-chat-style", "true");
      style.textContent = `
        .n8n-chat .chat-powered-by {
          font-size: 0 !important;
          line-height: 0 !important;
          margin-top: 0.5rem;
        }
        .n8n-chat .chat-powered-by::after {
          content: "© All Rights Reserved By Sharepal.";
          display: block;
          font-size: 0.72rem;
          line-height: 1rem;
          color: #6b7280;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-align: center;
        }
        .n8n-chat .chat-powered-by a {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
      injectedStyleRef.current = style;
    }

    // Apply chat CSS variables and remember previous values
    Object.entries(CHAT_CSS_VARIABLES).forEach(([key, value]) => {
      previousCssValuesRef.current[key] =
        document.documentElement.style.getPropertyValue(key);
      document.documentElement.style.setProperty(key, value);
    });

    let instance: unknown;

    let sessionId = localStorage.getItem("sessionId");

    if (!sessionId || typeof sessionId !== "string" || sessionId.length === 0) {
      sessionId = crypto.randomUUID();
      localStorage.setItem("sessionId", sessionId);
    }

    try {
      instance = createChat({
        webhookUrl,
        mode: "window",
        loadPreviousSession: true,
        showWelcomeScreen: true,
        initialMessages: [
          "Hey there! 👋",
          "I’m Sharepal’s assistant. How can I help you today?",
        ],
        i18n: {
          en: {
            title: "Need assistance?",
            subtitle: "Chat with our verification support team in real-time.",
            footer: "",
            getStarted: "Start a new conversation",
            inputPlaceholder: "Ask a question or describe an issue…",
            closeButtonTooltip: "Close chat",
          },
        },
        metadata: {
          source: "sharepal-admin",
          sessionId: sessionId,
          token: localStorage.getItem("token"),
        },
      });
      hasInitialized.current = true;
    } catch (error) {
      console.error("[N8nChatWidget] Failed to initialize chat widget:", error);
      // revert CSS if createChat fails
      Object.entries(previousCssValuesRef.current).forEach(([key, value]) => {
        document.documentElement.style.setProperty(key, value);
      });
      previousCssValuesRef.current = {};
      return;
    }

    if (
      instance &&
      typeof instance === "object" &&
      "destroy" in instance &&
      typeof (instance as any).destroy === "function"
    ) {
      destroyRef.current = () => {
        (instance as any).destroy();
      };
    } else {
      destroyRef.current = null;
    }

    return () => {
      hasInitialized.current = false;
      if (destroyRef.current) {
        destroyRef.current();
        destroyRef.current = null;
      }
      Object.entries(previousCssValuesRef.current).forEach(([key, value]) => {
        if (value) {
          document.documentElement.style.setProperty(key, value);
        } else {
          document.documentElement.style.removeProperty(key);
        }
      });
      previousCssValuesRef.current = {};
      if (injectedStyleRef.current) {
        injectedStyleRef.current.remove();
        injectedStyleRef.current = null;
      }
    };
  }, [user]);

  return null;
};

export default N8nChatWidget;
